import { useState, useEffect } from "react";
import { Menu as MenuIcon, X, ShoppingBag } from "lucide-react";
import { LOGO_URL } from "../data";
import { useCart } from "../context/CartContext";

const LINKS = [
  { label: "Especialidades", href: "#especialidades" },
  { label: "Menú", href: "#menu" },
  { label: "Galería", href: "#galeria" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Ubicación", href: "#ubicacion" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totalCount, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkColor = scrolled
    ? "text-brand-burgundy hover:text-brand-gold"
    : "text-brand-cream hover:text-brand-gold";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-cream/95 border-b border-brand-gold/25 py-2.5 shadow-sm"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="#inicio" data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="La Herencia Castañón"
            className="h-12 w-12 object-cover rounded-full ring-1 ring-brand-gold/50 bg-white group-hover:ring-brand-gold transition-all"
          />
          <span
            className={`hidden sm:block font-display leading-tight transition-colors ${
              scrolled ? "text-brand-burgundy" : "text-brand-cream"
            }`}
          >
            <span className="block text-brand-gold text-sm italic">La Herencia</span>
            <span className="block text-lg font-bold tracking-wide -mt-1">CASTAÑÓN</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className={`text-sm uppercase tracking-[0.15em] transition-colors duration-300 ${linkColor}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#menu"
            data-testid="header-order-btn"
            className="hidden md:inline-flex items-center bg-brand-gold text-brand-base font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full hover:bg-brand-goldlight transition-colors"
          >
            Ordenar
          </a>

          <button
            onClick={openCart}
            data-testid="header-cart-btn"
            aria-label="Ver carrito"
            className={`relative p-2.5 rounded-full transition-colors ${
              scrolled ? "text-brand-burgundy hover:bg-brand-gold/15" : "text-brand-cream hover:bg-white/10"
            }`}
          >
            <ShoppingBag className="w-6 h-6" />
            {totalCount > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-0.5 -right-0.5 bg-brand-gold text-brand-base text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center"
              >
                {totalCount}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
            className={`lg:hidden p-2 ${scrolled ? "text-brand-burgundy" : "text-brand-gold"}`}
          >
            {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-brand-cream border-t border-brand-gold/25 px-6 py-6 flex flex-col gap-5"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base uppercase tracking-[0.15em] text-brand-burgundy hover:text-brand-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#menu"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center bg-brand-gold text-brand-base font-bold uppercase tracking-widest text-sm px-5 py-3 rounded-full mt-2"
          >
            Ordenar ahora
          </a>
        </div>
      )}
    </header>
  );
};
